import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import DataTable, { TableColumn } from '../common/DataTable';
import EmptyState from '../common/EmptyState';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import {
  CRITICAL_RESULTS_ALERT_COLUMN_LABELS,
  CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY,
  CRITICAL_RESULTS_COLUMN_SORT_FIELD,
  type CriticalResultsAlertColumnKey,
  type CriticalResultsSortField,
} from '../../constants/criticalResultsDashboard.constants';
import type { CriticalResultsDashboardAlert } from '../../types/criticalResultsDashboard.types';
import {
  criticalityBadgeClass,
  displayStatusClass,
  displayStatusLabel,
  formatCriticalityLabel,
  formatShortDuration,
  formatSinceNotified,
  resolveAcknowledgementDisplayStatus,
} from '../../utils/criticalResultsDashboardUtils';
import { ICON_STROKE_WIDTH } from '../../constants';

function toDisplayString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function findAlertByRowId(
  alerts: CriticalResultsDashboardAlert[],
  rowId: number
): CriticalResultsDashboardAlert | undefined {
  for (const item of alerts) {
    if (item.id === rowId) {
      return item;
    }
  }
  return undefined;
}

interface CriticalResultsAlertsListPanelProps {
  title: string;
  alerts: CriticalResultsDashboardAlert[];
  totalCount: number;
  expectedKpiCount?: number | null;
  searchActive?: boolean;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  refreshing?: boolean;
  error: string | null;
  search: string;
  sortBy: CriticalResultsSortField;
  sortOrder: 'ASC' | 'DESC';
  variant?: 'overview' | 'drilldown';
  onSearchChange: (value: string) => void;
  onSortChange: (sortBy: CriticalResultsSortField, sortOrder: 'ASC' | 'DESC') => void;
  onPageChange: (page: number) => void;
  onAlertSelect?: (alert: CriticalResultsDashboardAlert) => void;
}

function isLikelyPatientName(value?: string | null): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }
  if (/^result\./i.test(trimmed)) {
    return false;
  }
  if (/study_instance/i.test(trimmed)) {
    return false;
  }
  return true;
}

function resolvePatientDisplay(alert: CriticalResultsDashboardAlert) {
  const rawName = alert.study?.patientName?.trim();
  const name = rawName && isLikelyPatientName(rawName) ? rawName : 'Unknown patient';
  const uhid = alert.study?.patientUhid?.trim() || '';
  return { name, uhid };
}

function formatStudy(alert: CriticalResultsDashboardAlert) {
  const displayName = alert.study?.studyDisplayName?.trim();
  if (displayName) {
    return displayName;
  }

  const bodyPart = alert.study?.testName?.trim() || alert.study?.studyDescription?.trim() || '';
  const modality = alert.study?.modalityName?.trim() || '';
  if (modality && bodyPart) {
    const bodyUpper = bodyPart.toUpperCase();
    const modalityUpper = modality.toUpperCase();
    if (bodyUpper.startsWith(modalityUpper)) {
      return bodyPart;
    }
    return `${modality} ${bodyPart}`;
  }
  return bodyPart || modality || '—';
}

function formatAccession(alert: CriticalResultsDashboardAlert) {
  return alert.study?.accessionNumber?.trim() || alert.study?.risVisitId?.trim() || '—';
}

function isAckTatOverdue(seconds?: number | null) {
  return seconds != null && seconds >= 30 * 60;
}

function formatAckTat(alert: CriticalResultsDashboardAlert): string {
  if (alert.acknowledgementTatSeconds == null) {
    return '—';
  }
  return formatShortDuration(alert.acknowledgementTatSeconds);
}

function alertToRow(alert: CriticalResultsDashboardAlert): Record<string, unknown> {
  const tableStatus = resolveAcknowledgementDisplayStatus(alert);
  const patient = resolvePatientDisplay(alert);
  return {
    id: alert.id,
    accession: formatAccession(alert),
    patientName: patient.name,
    patientUhid: patient.uhid,
    study: formatStudy(alert),
    criticality: formatCriticalityLabel(String(alert.criticalityBand)),
    criticalityBand: alert.criticalityBand,
    referringClinician: alert.study?.referredBy || '—',
    level: `L${alert.currentLevelNumber}`,
    status: tableStatus,
    sinceNotified: formatSinceNotified(alert.startedAt),
    ackTat: formatAckTat(alert),
    ackTatOverdue: isAckTatOverdue(alert.acknowledgementTatSeconds),
  };
}

function buildAlertTableColumns(
  alerts: CriticalResultsDashboardAlert[],
  onAlertSelect?: (alert: CriticalResultsDashboardAlert) => void,
  isOverview = false
): TableColumn<Record<string, unknown>>[] {
  const columns: TableColumn<Record<string, unknown>>[] = [
    { key: 'accession', label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.accession, width: 140 },
    { key: 'patient', label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.patient, width: 180 },
    { key: 'study', label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.study, width: 180 },
    {
      key: 'criticality',
      label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.criticality,
      width: 130,
      render: (_value, row) => (
        <span className={criticalityBadgeClass(toDisplayString(row.criticalityBand))}>
          {toDisplayString(row.criticality)}
        </span>
      ),
    },
    {
      key: 'referringClinician',
      label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.referringClinician,
      width: 160,
    },
    { key: 'level', label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.level, width: 80 },
    {
      key: 'status',
      label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.status,
      width: 130,
      render: value => (
        <span
          className={displayStatusClass(value as CriticalResultsDashboardAlert['displayStatus'])}
        >
          {displayStatusLabel(value as CriticalResultsDashboardAlert['displayStatus'])}
        </span>
      ),
    },
    { key: 'sinceNotified', label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.sinceNotified, width: 120 },
    {
      key: 'ackTat',
      label: CRITICAL_RESULTS_ALERT_COLUMN_LABELS.ackTat,
      width: 90,
      render: (value, row) => (
        <span className={row.ackTatOverdue ? 'crd-ack-tat--overdue' : undefined}>
          {toDisplayString(value)}
        </span>
      ),
    },
  ];

  if (!isOverview) {
    columns.push({
      key: 'details',
      label: 'Details',
      width: 90,
      render: (_value, row) => (
        <button
          type="button"
          className="crd-alert-details-link"
          onClick={event => {
            event.stopPropagation();
            const alert = findAlertByRowId(alerts, Number(row.id));
            if (alert) {
              onAlertSelect?.(alert);
            }
          }}
        >
          View →
        </button>
      ),
    });
  }

  return columns;
}

function formatAlertsCountLabel(totalCount: number, searchActive: boolean): string {
  const plural = totalCount === 1 ? '' : 's';
  const suffix = searchActive ? ' matching search' : '';
  return `${totalCount} alert${plural}${suffix}`;
}

function AlertsPanelHeader({
  title,
  loading,
  totalCount,
  searchActive,
  searchDraft,
  onSearchDraftChange,
  onSearchChange,
  columnsRef,
  columnsOpen,
  onToggleColumns,
  toggleableColumns,
  visibleColumns,
  onVisibleColumnsChange,
  isOverview = false,
}: Readonly<{
  title: string;
  loading: boolean;
  totalCount: number;
  searchActive: boolean;
  searchDraft: string;
  onSearchDraftChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  columnsRef: React.RefObject<HTMLDivElement>;
  columnsOpen: boolean;
  onToggleColumns: () => void;
  toggleableColumns: CriticalResultsAlertColumnKey[];
  visibleColumns: typeof CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY;
  onVisibleColumnsChange: React.Dispatch<
    React.SetStateAction<typeof CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY>
  >;
  isOverview?: boolean;
}>) {
  return (
    <header className="crd-panel-header crd-alerts-table-header">
      <div className="crd-alerts-table-heading">
        <h2 className="crd-panel-title">{title}</h2>
        {!isOverview && !loading && totalCount >= 0 ? (
          <p className="crd-alerts-count">{formatAlertsCountLabel(totalCount, searchActive)}</p>
        ) : null}
      </div>
      <div className="crd-alerts-toolbar">
        {isOverview ? null : (
          <SearchBar
            variant="light"
            placeholder="Search alerts"
            value={searchDraft}
            onChange={onSearchDraftChange}
            onSearch={onSearchChange}
          />
        )}
        <div
          className="crd-columns-wrap"
          ref={columnsRef}
        >
          <button
            type="button"
            className="crd-btn crd-btn--secondary crd-columns-button"
            onClick={onToggleColumns}
          >
            Columns
            <ChevronDown
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
          {columnsOpen ? (
            <div
              className="crd-columns-menu"
              role="menu"
            >
              {toggleableColumns.map(columnKey => (
                <label
                  key={columnKey}
                  className="crd-columns-option"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns[columnKey]}
                    onChange={event =>
                      onVisibleColumnsChange(current => ({
                        ...current,
                        [columnKey]: event.target.checked,
                      }))
                    }
                  />
                  <span>{CRITICAL_RESULTS_ALERT_COLUMN_LABELS[columnKey]}</span>
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function AlertsSortRow({
  toggleableColumns,
  visibleColumns,
  sortBy,
  sortOrder,
  onSortToggle,
}: Readonly<{
  toggleableColumns: CriticalResultsAlertColumnKey[];
  visibleColumns: typeof CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY;
  sortBy: CriticalResultsSortField;
  sortOrder: 'ASC' | 'DESC';
  onSortToggle: (columnKey: CriticalResultsAlertColumnKey) => void;
}>) {
  return (
    <div
      className="crd-sort-row"
      aria-label="Sortable columns"
    >
      {toggleableColumns
        .filter(
          columnKey => visibleColumns[columnKey] && CRITICAL_RESULTS_COLUMN_SORT_FIELD[columnKey]
        )
        .map(columnKey => {
          const mappedSort = CRITICAL_RESULTS_COLUMN_SORT_FIELD[columnKey]!;
          const isActive = sortBy === mappedSort;
          let sortMarker = '';
          if (isActive) {
            sortMarker = sortOrder === 'ASC' ? ' ↑' : ' ↓';
          }
          return (
            <button
              key={columnKey}
              type="button"
              className={['crd-sort-chip', isActive ? 'crd-sort-chip--active' : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSortToggle(columnKey)}
            >
              {CRITICAL_RESULTS_ALERT_COLUMN_LABELS[columnKey]}
              {sortMarker}
            </button>
          );
        })}
    </div>
  );
}

function getAlertsEmptyDescription(isOverview: boolean, searchActive: boolean): string {
  if (isOverview) {
    return 'No critical alerts match the current filters for this date range.';
  }
  if (searchActive) {
    return 'No alerts match your search. Try different terms or clear search.';
  }
  return 'No alerts match the current filters for this category.';
}

function AlertsTableContent({
  loading,
  refreshing,
  searchActive,
  alerts,
  columns,
  tableData,
  onAlertSelect,
  isOverview = false,
}: Readonly<{
  loading: boolean;
  refreshing: boolean;
  searchActive: boolean;
  alerts: CriticalResultsDashboardAlert[];
  columns: TableColumn<Record<string, unknown>>[];
  tableData: Record<string, unknown>[];
  onAlertSelect?: (alert: CriticalResultsDashboardAlert) => void;
  isOverview?: boolean;
}>) {
  if (loading) {
    return <p className="crd-table-placeholder">Loading alerts…</p>;
  }
  if (alerts.length === 0) {
    return (
      <EmptyState
        title={isOverview ? 'No critical alerts' : 'No active critical alerts'}
        description={getAlertsEmptyDescription(isOverview, searchActive)}
      />
    );
  }
  return (
    <div
      className={[
        'crd-data-table-wrap',
        isOverview ? 'crd-data-table-wrap--overview' : '',
        refreshing ? 'crd-data-table-wrap--refreshing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {refreshing ? <span className="crd-table-refresh-indicator">Updating…</span> : null}
      <DataTable
        columns={columns}
        data={tableData}
        fullWidth={isOverview}
        getRowKey={row => Number(row.id)}
        onRowClick={row => {
          const alert = findAlertByRowId(alerts, Number(row.id));
          if (alert) {
            onAlertSelect?.(alert);
          }
        }}
      />
    </div>
  );
}

function hasCountMismatch(
  expectedKpiCount: number | null | undefined,
  searchActive: boolean,
  loading: boolean,
  totalCount: number
): boolean {
  return expectedKpiCount != null && !searchActive && !loading && totalCount !== expectedKpiCount;
}

export default function CriticalResultsAlertsListPanel({
  title,
  alerts,
  totalCount,
  expectedKpiCount = null,
  searchActive = false,
  currentPage,
  pageSize,
  loading,
  refreshing = false,
  error,
  search,
  sortBy,
  sortOrder,
  onSearchChange,
  onSortChange,
  onPageChange,
  onAlertSelect,
  variant = 'drilldown',
}: Readonly<CriticalResultsAlertsListPanelProps>) {
  const isOverview = variant === 'overview';
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY
  );
  const columnsRef = useRef<HTMLDivElement>(null);
  const [searchDraft, setSearchDraft] = useState(search);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      if (searchDraft !== search) {
        onSearchChange(searchDraft);
      }
    }, 400);
    return () => globalThis.clearTimeout(timer);
  }, [searchDraft, search, onSearchChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSortToggle = (columnKey: CriticalResultsAlertColumnKey) => {
    const mappedSort = CRITICAL_RESULTS_COLUMN_SORT_FIELD[columnKey];
    if (!mappedSort) {
      return;
    }
    if (sortBy === mappedSort) {
      onSortChange(mappedSort, sortOrder === 'ASC' ? 'DESC' : 'ASC');
      return;
    }
    onSortChange(mappedSort, 'DESC');
  };

  const allColumns: TableColumn<Record<string, unknown>>[] = useMemo(
    () => buildAlertTableColumns(alerts, onAlertSelect, isOverview),
    [alerts, isOverview, onAlertSelect]
  );

  const columns = useMemo(
    () => allColumns.filter(column => visibleColumns[column.key as CriticalResultsAlertColumnKey]),
    [allColumns, visibleColumns]
  );

  const tableData = useMemo(() => alerts.map(alertToRow), [alerts]);
  const toggleableColumns = Object.keys(
    CRITICAL_RESULTS_ALERT_COLUMN_LABELS
  ) as CriticalResultsAlertColumnKey[];
  const countMismatch = hasCountMismatch(expectedKpiCount, searchActive, loading, totalCount);

  return (
    <section
      className={[
        'crd-panel',
        'crd-alerts-table-section',
        isOverview ? 'crd-alerts-table-section--overview' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <AlertsPanelHeader
        title={title}
        loading={loading}
        totalCount={totalCount}
        searchActive={searchActive}
        searchDraft={searchDraft}
        onSearchDraftChange={setSearchDraft}
        onSearchChange={onSearchChange}
        columnsRef={columnsRef}
        columnsOpen={columnsOpen}
        onToggleColumns={() => setColumnsOpen(open => !open)}
        toggleableColumns={toggleableColumns}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={setVisibleColumns}
        isOverview={isOverview}
      />

      {!isOverview && countMismatch ? (
        <output className="crd-count-note">
          Dashboard KPI shows {expectedKpiCount} for this category. Counts can differ when list
          filters or pagination apply.
        </output>
      ) : null}

      {isOverview ? null : (
        <AlertsSortRow
          toggleableColumns={toggleableColumns}
          visibleColumns={visibleColumns}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortToggle={handleSortToggle}
        />
      )}

      {error && !loading ? <p className="crd-error">{error}</p> : null}

      <AlertsTableContent
        loading={loading}
        refreshing={refreshing}
        searchActive={searchActive}
        alerts={alerts}
        columns={columns}
        tableData={tableData}
        onAlertSelect={onAlertSelect}
        isOverview={isOverview}
      />

      {!loading && totalCount > 0 ? (
        <Pagination
          currentPage={currentPage}
          totalItems={totalCount}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
        />
      ) : null}
    </section>
  );
}
